// src/components/InviteStaffModal.js
import React, { useState } from 'react';
import {
    Modal, Box, Typography, TextField, Button, Stack, CircularProgress, Alert,
    Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import logger from '../utils/logger';
import authFetch from '../utils/authFetch'; // Assuming authFetch is in utils

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 450 }, // Responsive width
  bgcolor: 'background.paper',
  borderRadius: '8px',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
};

// These roles should align with your backend UserRole enum and business logic
// Fetch these from an API or define in a shared constants/enums file for consistency
const staffRoles = [
  { value: 'physician_qhp', label: 'Physician (QHP)' },
  { value: 'clinical_staff', label: 'Clinical Staff' },
  { value: 'tenant_admin', label: 'Tenant Admin' },
];

const sendStaffInviteAPI = async ({ firstName, lastName, email, role }) => {
    logger.info("InviteStaffModal: Calling backend to invite staff", { firstName, lastName, email, role });
    const payload = {
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role_to_assign: role,
    };
    return authFetch('/api/admin/users/invite-staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

};

const InviteStaffModal = ({ open, onClose, onInviteSent }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleRoleChange = (event) => {
    setFormData(prev => ({ ...prev, role: event.target.value }));
    if (error) setError('');
  };

  const validateForm = () => {
    const { firstName, lastName, email, role } = formData;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !role) {
      setError("All fields are required.");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await sendStaffInviteAPI(formData);
      const successMsg = response.message || `Invitation successfully sent to ${formData.email}!`;
      logger.info('InviteStaffModal: Staff invite API call successful.', response);
      setSuccess(successMsg);
      if (onInviteSent) {
        onInviteSent({ type: 'success', message: successMsg });
      }
      setTimeout(() => {
        onClose(); // Close modal after a delay
      }, 3000); // 3-second delay to show the message
    } catch (err) {
      const errorMsg = err.detail || err.message || 'Failed to invite staff. Please try again.';
      logger.error('InviteStaffModal: Staff invitation failed', err);
      setError(errorMsg);
      if (onInviteSent) {
        onInviteSent({ type: 'error', message: errorMsg });
      }
    } finally {
      setIsLoading(false);
    }
  };


  
  React.useEffect(() => {
    if (open) {
      setFormData({ firstName: '', lastName: '', email: '', role: '' });
      setError('');
      setSuccess('');
      setIsLoading(false);
    }
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="invite-staff-modal-title">
      <Box sx={style}>
        <Typography id="invite-staff-modal-title" variant="h6" component="h2" gutterBottom>
          Invite New Staff Member
        </Typography>
        <TextField
          label="First Name"
          name="firstName"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.firstName}
          onChange={handleInputChange}
          required
        />
        <TextField
          label="Last Name"
          name="lastName"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.lastName}
          onChange={handleInputChange}
          required
        />
        <TextField
          label="Email Address"
          name="email"
          type="email"
          variant="outlined"
          fullWidth
          margin="normal"
          value={formData.email}
          onChange={handleInputChange}
          required
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="staff-role-select-label">Role</InputLabel>
          <Select
            labelId="staff-role-select-label"
            name="role"
            value={formData.role}
            label="Role"
            onChange={handleRoleChange}
          >
            {staffRoles.map((r) => (
              <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {success && <Alert severity="success" sx={{ mt: 1, mb: 1 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{error}</Alert>}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }} justifyContent="flex-end">
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Invitation'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default InviteStaffModal;